import { defineStore } from 'pinia';

let cartLoadPromise = null;

/**
 * Cart store for V2 API
 * Stores cart data in the V2 format directly from the API
 */
export const cartV2Store = defineStore('cartV2', {
  state: () => ({
    // V2 cart data structure
    id: null,
    items: [],
    totals: {
      subtotal: 0,
      total: 0,
      count: 0,
    },
    expires_at: null,
    metadata: null,
    lastLoadedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    /**
     * Get total number of tickets in cart
     */
    ticketCount: (state) => state.totals?.count || 0,

    /**
     * Get total amount in cart
     */
    totalAmount: (state) => parseFloat(state.totals?.total || 0),

    /**
     * Get all ticket items grouped by event
     */
    ticketsByEvent: (state) => {
      const grouped = {};

      for (const item of state.items || []) {
        if (item.item_type !== 'ticket') continue;

        const eventId = item.metadata?.event_id;
        if (!eventId) continue;

        if (!grouped[eventId]) {
          grouped[eventId] = {
            eventId: parseInt(eventId),
            eventTitle:
              item.metadata?.event_title || item.metadata?.event_name || '',
            eventDate: item.metadata?.event_date || '',
            eventTime: item.metadata?.event_time || '',
            items: [],
          };
        }

        grouped[eventId].items.push(item);
      }

      return Object.values(grouped);
    },

    /**
     * Check if cart has any items
     */
    hasItems: (state) => (state.items?.length || 0) > 0,

    /**
     * Check if cart is expired
     */
    isExpired: (state) => {
      if (!state.expires_at) return false;
      return new Date(state.expires_at) < new Date();
    },

    /**
     * Check if cart has been loaded from the backend
     */
    isLoaded: (state) => state.lastLoadedAt !== null,
  },

  actions: {
    /**
     * Set cart data from V2 API response
     */
    setCart(cartData) {
      this.id = cartData.id || null;
      this.items = cartData.items || [];
      this.totals = cartData.totals || { subtotal: 0, total: 0, count: 0 };
      this.expires_at = cartData.expires_at || null;
      this.metadata = cartData.metadata || null;
      this.lastLoadedAt = Date.now();
      this.error = null;
    },

    /**
     * Clear cart
     */
    clearCart() {
      this.id = null;
      this.items = [];
      this.totals = { subtotal: 0, total: 0, count: 0 };
      this.expires_at = null;
      this.metadata = null;
      this.lastLoadedAt = null;
      this.error = null;
    },

    /**
     * Check whether the cart should be refreshed from the backend
     */
    needsRefresh(maxAgeMs = 30000) {
      if (this.lastLoadedAt === null) {
        return true;
      }

      return Date.now() - this.lastLoadedAt > maxAgeMs;
    },

    /**
     * Set loading state
     */
    setLoading(loading) {
      this.loading = loading;
    },

    /**
     * Set error
     */
    setError(error) {
      this.error = error;
    },

    /**
     * Load the cart from the backend with cache + in-flight request dedupe.
     */
    async loadCart({ force = false, maxAgeMs = 30000 } = {}) {
      if (!force && !this.needsRefresh(maxAgeMs)) {
        return {
          id: this.id,
          items: this.items,
          totals: this.totals,
          expires_at: this.expires_at,
          metadata: this.metadata,
        };
      }

      if (cartLoadPromise) {
        return cartLoadPromise;
      }

      cartLoadPromise = (async () => {
        try {
          this.setLoading(true);

          const response = await fetch('/api/v2/cart');
          if (!response.ok) {
            this.setError(`Failed to load cart: ${response.status}`);
            return null;
          }

          const data = await response.json();
          this.setCart(data);
          return data;
        } catch (error) {
          this.setError(error.message);
          return null;
        } finally {
          this.setLoading(false);
          cartLoadPromise = null;
        }
      })();

      return cartLoadPromise;
    },
  },
});
